import styled from '@emotion/styled';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    Checkbox,
    HeadingText40,
    Input,
    InputType,
} from '@modernhealth/vital-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, omit } from 'lodash';

const CheckboxContainer = styled.div({
    marginBottom: '24px',
    marginTop: '24px',
    width: '100%',
});

const CheckboxWrapper = styled.div({
    marginBottom: '8px',
    marginTop: '4px',
    width: '100%',
});

export enum SessionSurveyModalQuestionsTestId {
    Title = 'SessionSurveyModalQuestionsTestId.Title',
    Description = 'SessionSurveyModalQuestionsTestId.Description',
}

interface SessionSurveyModalQuestionsProps {
    onSkip: VoidFunction;
    handleSubmit: VoidFunction;
    otherText: string;
    setOtherText: React.Dispatch<React.SetStateAction<string>>;
    selectedReasons: object;
    setSelectedReasons: React.Dispatch<React.SetStateAction<object>>;
}

interface Reason {
    value: string;
    label: string;
}

export const SessionSurveyModalQuestions: React.FC<SessionSurveyModalQuestionsProps> = ({
    onSkip,
    handleSubmit,
    otherText,
    setOtherText,
    selectedReasons,
    setSelectedReasons,
}) => {
    const { t } = useTranslation();
    const reasons = [
        { value: 'COST', label: t('videoSessions:session_survey_modal.answer_one') },
        { value: 'UNEXPECTED_PAYMENT', label: t('videoSessions:session_survey_modal.answer_two') },
        { value: 'JUST_EXPLORING', label: t('videoSessions:session_survey_modal.answer_three') },
        { value: 'LATER', label: t('videoSessions:session_survey_modal.answer_four') },
        { value: 'OTHER', label: t('videoSessions:session_survey_modal.other') },
    ];
    const isOtherSelected = selectedReasons['OTHER'];
    const isSubmitDisabled = isEmpty(selectedReasons) || (isOtherSelected && otherText.length < 3);

    const handleCheckboxClick = (reason: Reason) => {
        const isSelected = selectedReasons[reason.value];
        const reasonsToSet = isSelected
            ? omit(selectedReasons, reason.value)
            : { ...selectedReasons, [reason.value]: !isSelected };
        setSelectedReasons(reasonsToSet);
    };

    return (
        <>
            <HeadingText40 testID={SessionSurveyModalQuestionsTestId.Title}>
                {t('videoSessions:session_survey_modal.title')}
            </HeadingText40>
            <BodyTextMedium
                marginBottom={16}
                testID={SessionSurveyModalQuestionsTestId.Description}
            >
                {t('videoSessions:session_survey_modal.description')}
            </BodyTextMedium>
            <CheckboxContainer>
                {reasons.map(reason => (
                    <CheckboxWrapper key={reason.label}>
                        <Checkbox
                            key={reason.label}
                            label={reason.label}
                            onChange={() => handleCheckboxClick(reason)}
                            value={selectedReasons[reason.value]}
                        />
                    </CheckboxWrapper>
                ))}
                {isOtherSelected && (
                    <Input
                        fieldId="support_question"
                        type={InputType.TextArea}
                        label={t('videoSessions:session_survey_modal.other_helper_text')}
                        onChange={(value: string) => setOtherText(value)}
                        value={otherText}
                        useDefaultPlaceholder
                    />
                )}
            </CheckboxContainer>

            <ButtonGroup>
                <Button
                    variant="secondary"
                    size="medium"
                    children={t('videoSessions:session_survey_modal.button_cancel')}
                    onClick={onSkip}
                />
                <Button
                    variant="primary"
                    size="medium"
                    children={t('videoSessions:session_survey_modal.button_submit')}
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit}
                />
            </ButtonGroup>
        </>
    );
};
